import { Money } from "../product/Money";
import {
    KlarnaData,
    SchemeData,
} from "../../frontend/components/genius-ui/organisms/checkout/provider/payment/types";

export enum PaymentStatuses {
    INIT = "init",
    PENDING = "pending",
    PAID = "paid",
    FAILED = "failed",
}

export type PaymentMethodType =
    | "card"
    | "klarna"
    | "klarna_account"
    | "klarna_paynow"
    | "paysafecard"
    | "swish"
    | "trustly"
    | "vipps"
    | "multibanco"
    | "invoice"
    | "installment"
    | "paypal"
    | "amazon"
    | "eps"
    | "twint"
    | "ideal"
    | "gpay"
    | "pfcard"
    | "pfefinance"
    | "applepay"
    | "vorkasse"
    | "cod"
    | "paypalExpress"

export enum PaymentMethods {
    Card = "card",
    Klarna = "klarna",
    Invoice = "invoice",
    Installment = "installment",
    Paypal = "paypal",
    AmazonPay = "amazon",
    Twint = "twint",
    PF_CARD = "pfcard",
    PF_FINANCE = "pfefinance",
    EPS = "eps",
    GooglePay = "gpay",
    CASH_ON_DELIVERY = "cod",
    ApplePay = "applepay",
    VORKASSE = "vorkasse",
    Paypal_Express = "paypalExpress",
}

export enum PaymentNames {
    Invoice = "Invoice",
    Installment = "Installment payment",
    Paypal = "Paypal",
    AmazonPay = "Amazon Pay",
    Twint = "Twint",
    PF_CARD = "Post Finance Card",
    EPS = "EPS",
    ApplePay = "Apple Pay",
    Vorkasse = "Vorkasse",
}

export interface PaymentMethod {
    name: string;
    type: PaymentMethodType;
    image: {
        src: string;
    };
    enableExpressCheckout: boolean;
    showInDE_DE?: boolean;
    showInDE_AT?: boolean;
    showInDE_CH?: boolean;
}

export interface RedirectAction {
    type: "redirect";
    method: string;
    data?: unknown;
    url: string;
    paymentMethodType: string;
}

export interface ThreeDS2Action {
    type: "threeDS2";
    authorisationToken: string;
    paymentData: string;
    paymentMethodType: string;
    subtype: string;
    token: string;
    url: string;
}
export type PaymentAction = RedirectAction | ThreeDS2Action;

export type PaymentResponse = {
    createCheckout: Record<string, any>;
    signature: string | null;
    payments: Record<string, string>;
    paypal: Record<string, string>;
    additionalData: Record<string, string>;
    pspReference: string;
    resultCode:
        | "Authorised"
        | "Cancelled"
        | "Error"
        | "Refused"
        | "RedirectShopper"
        | "IdentifyShopper"
        | "ChallengeShopper";
    merchantReference: string;
    action?: PaymentAction;
};

export interface SchemePaymentRequestPayload {
    amount: {
        currency: string;
        value: number;
    };
    reference: string;
    paymentMethod: SchemeData;
    returnUrl?: string;
    channel: "web";
    origin: string;
    countryCode: string;
    shopperLocale?: string;
    browserInfo: {
        acceptHeader: string;
        colorDepth: number;
        javaEnabled: boolean;
        javaScriptEnabled?: boolean;
        language: string;
        screenHeight: number;
        screenWidth: number;
        timeZoneOffset: number;
        userAgent: string;
    };
    authenticationData?: {
        threeDSRequestData: {
            nativeThreeDS: "preferred";
        };
    };
    metadata?: Record<string, unknown>;
}

export interface KlarnaLineItem {
    id: string;
    quantity: string;
    description: string;
    amountIncludingTax: number | string;
    productUrl?: string;
    imageUrl?: string;
}

export interface KlarnaPaymentRequestPayload {
    amount: {
        currency: string;
        value: number;
    };
    reference: string;
    paymentMethod: Pick<KlarnaData, "type">;
    returnUrl: string;
    countryCode: string;
    shopperLocale: string;
    shopperReference: string;
    shopperEmail: string;
    shopperName?: {
        firstName: string;
        lastName?: string;
    };
    lineItems: Array<KlarnaLineItem>;
    metadata?: Record<string, unknown>;
}

export interface UnzerInstallmentCreationPayload {
    holder: string;
    iban: string;
    country: string;
    inquiryId: string;
    numberOfRates: string;
}

export interface PaypalOrderCreationPayload {
    cartId: string;
    returnUrl: string;
    isCartPage: Boolean;
    isFirstAttempt?:Boolean
    orderId?:string
}

export interface PaypalAuthOrderPayload {
    cartId?: string;
    orderId: string;
    isCartPage?: Boolean;
}

export interface AmazonPayCreateCheckoutSessionPayload {
    webCheckoutDetails: any;
    paymentDetails: any;
}
export interface AmazonPayGetCheckoutSessionPayload {
    checkoutSessionId: string;
    cartId: string;
    url: string;
}
export interface AmazonPayVerifySessionPayload {
    cartId: string;
    amount: {
        currency: string;
        value: number;
    };
    sessionId: string;
}

export type PaymentRequestPayload =
    | SchemePaymentRequestPayload
    | KlarnaPaymentRequestPayload
    | UnzerInstallmentCreationPayload
    | PaypalOrderCreationPayload;
export interface Payment {
    /**
     * An internal ID to identify this payment
     */
    id: string;

    /**
     * The name of the payment service provider
     */
    paymentProvider: string;

    /**
     * The ID used by the payment service provider for this payment
     */
    paymentId: string;

    /**
     * How much money this payment intends to receive from the customer. The value usually matches the cart or order gross total.
     */
    amountPlanned: Money;

    /**
     * A text describing the current status of the payment
     */
    debug?: string;

    /**
     * One of the `PaymentStatuses` constants
     */
    paymentStatus: string;

    version?: number;

    /**
     * The descriptor of the payment method used for this payment
     */
    paymentMethod: string;

    // TODO: do we need paymentDetails if not using custom fields?
    paymentDetails?: [];
    cardSummary: string;
}

export enum TRANSACTION_STATUS {
    PENDING = "Pending",
    FAILURE = "Failure",
    SUCCESS = "Success",
}

export enum TRANSACTION_TYPE {
    CHARGE = "Charge",
    AUTH = "Authorization",
}

export enum PAYMENT_ACTION {
    REDIRECT = "redirect",
}

export enum PAYMENT_FILEDS {
    RESPONSE = 'paymentResponse',
    '3DS' = '3DSRequest',
    '3DSResponse' = '3DSResponse',
    REQUEST = 'paymentRequest',
    CHANGE_AMOUNT = 'changeAmountPlanned',
  }

export interface UnzerInstallmentPlan {
    numberOfRates: number;
    totalAmount: string;
    nominalInterestRate: number;
    effectiveInterestRate: number;
    minimumInstallmentFee: string;
    secciUrl: string;
    installmentRates: UnzerInstallmentRate[];
}

export interface UnzerInstallmentRate {
    date: string;
    rate: string;
}
